/**
 * FriendsGrid component
 * Unified FlashList grid component for displaying friends with responsive columns
 * Simple implementation with type-based rendering
 */

import React, { useMemo, useCallback } from 'react'
import { View, StyleSheet } from 'react-native'
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list'
import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../../src/shared/constants/flashList.constants'
import { StaggeredFadeIn } from '../../shared/StaggeredFadeIn'
import {
  friendsGridStyles,
  getFlashListStyle,
  getCardContainerStyle,
  CARD_GAP,
} from './FriendsGrid.styles'
import type { FriendsGridItem } from '../../../src/friends/helpers/unifiedList.helper'

interface FriendsGridProps {
  items: FriendsGridItem[]
  renderCard: (props: { item: FriendsGridItem; index: number; cardWidth: number }) => React.ReactElement | null
  numColumns: number
  screenWidth: number
  /** When set, grid uses this width (content area) so cards align centered with equal side padding. */
  contentWidth?: number
  /** When this changes, FlashList re-renders items (e.g. selection state). Avoids blank cells on toggle. */
  extraData?: unknown
  /** Test ID for testing */
  testID?: string
}

export const FriendsGrid: React.FC<FriendsGridProps> = ({
  items,
  renderCard,
  numColumns,
  screenWidth,
  contentWidth,
  extraData,
  testID,
}) => {
  const listWidth = contentWidth ?? screenWidth

  const { cardWidth } = useMemo(() => {
    const maxCardWidthPerColumn = 200

    const availableWidth = listWidth
    const totalGaps = (numColumns - 1) * CARD_GAP
    const calculatedWidth = (availableWidth - totalGaps) / numColumns

    const width =
      contentWidth != null
        ? calculatedWidth
        : Math.min(calculatedWidth, maxCardWidthPerColumn)

    return {
      cardWidth: width,
    }
  }, [listWidth, numColumns, contentWidth])

  const cardContainerStyle = useMemo(
    () => getCardContainerStyle(cardWidth),
    [cardWidth]
  )

  const flashListStyle = useMemo(
    () => StyleSheet.flatten([friendsGridStyles.flashList, getFlashListStyle(listWidth)]),
    [listWidth]
  )

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<FriendsGridItem>) => {
      const isLastInRow = (index + 1) % numColumns === 0

      return (
        <View
          style={[
            cardContainerStyle,
            isLastInRow ? friendsGridStyles.cardContainerLastInRow : friendsGridStyles.cardContainerNotLastInRow,
          ]}
        >
          <StaggeredFadeIn index={index}>
            {renderCard({ item, index, cardWidth })}
          </StaggeredFadeIn>
        </View>
      )
    },
    [numColumns, cardContainerStyle, renderCard, cardWidth]
  )

  return (
    <FlashList
      overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
      data={items}
      numColumns={numColumns}
      extraData={extraData}
      key={numColumns}
      testID={testID || 'friends-grid'}
      renderItem={renderItem}
      style={flashListStyle}
      showsVerticalScrollIndicator={false}
      keyExtractor={keyExtractor}
    />
  )
}

const keyExtractor = (item: FriendsGridItem) => item.id

